<template>
  <CommentBubble v-if="!draft" :type="'note'" :modelValue="note.noteId + ' ' + note.text">
    <slot>
      <div>
        <div class="flex gap-2 items-center baseline text-sm">
          <span>{{ formatDate(note.createdAt, locale) }}</span>
        </div>

        <div v-if="!isEditing" :class="note.text.length <= 40 ? 'text-xl p-4' : 'text-m p-4'">
          {{ getTextForLocale(note.text) }}
        </div>
        <div v-else>
          <SimpleTextArea
            v-model="localText"
            name="note"
            class="text-input"
            placeholder="Deine persönliche Notiz..."
            multiLanguage
          ></SimpleTextArea>
        </div>

        <div class="footer">
          <div class="actions">
            <Button
              v-if="!isEditing"
              icon="pi pi-pencil"
              class="primary p-button-text p-button-sm"
              :disabled="!profileStore.userProfile?.isSeyboldUser"
              @click="startEdit"
            />
            <Button
              v-if="isEditing"
              icon="pi pi-times"
              class="p-button-secondary p-button-text p-button-sm"
              @click="cancelEdit"
            />
            <Button
              icon="pi pi-trash"
              class="p-button-danger p-button-text p-button-sm"
              :disabled="!profileStore.userProfile?.isSeyboldUser"
              @click="remove"
            />
            <Button
              v-if="isEditing"
              icon="pi pi-check"
              class="p-button-sm"
              :disabled="!profileStore.userProfile?.isSeyboldUser"
              @click="save"
            />
          </div>
        </div>
      </div>
    </slot>
  </CommentBubble>
  <CommentBubble
    v-else
    v-model="draftTextLocal"
    :type="'note'"
    :editable="true"
    :draft="true"
    @submit="onDraftSave"
    @blur="onDraftBlur"
  >
    <slot />
    <div class="footer">
      <div class="actions">
        <Button
          icon="pi pi-check"
          class="p-button-sm"
          :disabled="!draftTextTrimmed.length || !profileStore.userProfile?.isSeyboldUser"
          @click="onDraftSave"
        />
      </div>
    </div>
  </CommentBubble>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useMultilingualText } from '@/composables/useMultilingualText';
import { type Note, useNotesStore } from '@/stores/localNotes';
import { useProfileStore } from '@/stores/profile';
import { formatDate } from '@/utils/dateUtils';

const props = defineProps<{
  note: Note;
  dataKey: string;
  id: string | number;
  draft?: boolean;
  draftText?: string;
}>();

const emit = defineEmits<{
  (e: 'delete', noteId: number): void;
  (e: 'update:draftText', v: string): void;
  (e: 'draft-save'): void;
  (e: 'draft-blur'): void;
  (e: 'edit-start', id: number): void;
  (e: 'edit-end', id: number): void;
}>();

const { locale } = useI18n();
const profileStore = useProfileStore();
const { getTextForLocale } = useMultilingualText();
const store = useNotesStore();
const isEditing = ref(false);
const localText = ref(props.note.text);

const draftTextLocal = computed({
  get: () => props.draftText ?? '',
  set: (v: string) => emit('update:draftText', v),
});
const draftTextTrimmed = computed(() => (draftTextLocal.value ?? '').trim());

// Usual note
function startEdit() {
  localText.value = props.note.text;
  isEditing.value = true;
  emit('edit-start', props.note.noteId);
}

function cancelEdit() {
  isEditing.value = false;
  emit('edit-end', props.note.noteId);
}

function save() {
  store.updateNote(props.dataKey, props.id, props.note.noteId, localText.value);
  isEditing.value = false;
  emit('edit-end', props.note.noteId);
}

function remove() {
  emit('delete', props.note.noteId);
}

// ——— Draft-note ———
function onDraftSave() {
  if (!draftTextTrimmed.value.length) return;
  emit('draft-save');
}
function onDraftBlur() {
  if (!draftTextTrimmed.value.length) return;
  emit('draft-blur');
}
</script>

<style scoped lang="css">
/* :deep() */
.text-input {
  border: none !important;
  background-color: transparent !important;
  padding: none !important;
}

/* :deep() */
.text-input label {
  display: none;
}

.comment-container {
  display: flex;
  flex-direction: column;
}

.comment-wrapper {
  max-width: 65%;
  margin: 1rem;
  padding: 1rem;
  position: relative;
  font-size: 0.95rem;
  line-height: 1.5;
}

.note-style {
  background-color: #fff8c6;
  color: #4a4a00;
  border: 1px solid #f2e08c;
  border-radius: 6px;
}

.text-input {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 0.5rem;
  padding: 0.5rem;
  resize: vertical;
  font-size: 0.95rem;
  background: #fff;
}

.footer {
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}
.actions {
  display: flex;
  gap: 0.25rem;
}
.text-content {
  white-space: pre-wrap;
}
</style>
